import { httpClient } from '@/core/api/httpClient';
import { useAuthStore } from '@/core/stores/auth.store';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { AxiosError, isCancel } from 'axios';
import { sileo } from 'sileo';
import i18n from '@/core/i18n/config';
import { classifyHttpError, extractBackendErrorCode } from '@/core/api/http-errors';
import { lookupRegistry } from '@/app/error-registry';
import { adapter } from '@/features/auth/public';
import { registerCoreTranslations } from '@/core/i18n/locales';
import { registerAuthTranslations } from '@/features/auth/public';
import { setupZodErrorMap } from '@/core/i18n/zodErrorMap';
import { registerLayoutTranslations } from '@/shared/components/custom/layout/locales';
import { registerListViewTranslations } from '@/shared/components/custom/list-view/locales';
import { registerAsyncComboboxTranslations } from '@/shared/components/custom/async-combobox/locales';
import { registerOnboardingTranslations } from '@/features/organizations/public';
import { registerOrganizationsTranslations } from '@/features/organizations/public';
import { registerMembersTranslations } from '@/features/members/public';
import { registerClientsTranslations } from '@/features/clients/public';
import { registerClientContactsTranslations } from '@/features/client-contacts/public';
import { registerClientMembersTranslations } from '@/features/client-members/public';
import { registerCalendarTranslations } from '@/features/calendar/public';
import { registerTasksTranslations } from '@/features/tasks/public';
import { registerTimeTrackingTranslations } from '@/features/time-tracking/public';
import { registerProjectsTranslations } from '@/features/projects/public';
import { registerOrganizationUnitsTranslations } from '@/features/organization-units/public';
import { registerPaginationTranslations } from '@/shared/components/custom/pagination/locales';
import { registerFormFieldTranslations } from '@/shared/components/custom/form-field/locales';
import { registerColorPickerTranslations } from '@/shared/components/custom/color-picker/locales';
import { registerSettingsTranslations } from '@/features/settings/public';
import { registerTagsTranslations } from '@/features/tags/public';
import { registerBoardsTranslations } from '@/features/boards/public';
import { registerWhiteboardsTranslations } from '@/features/whiteboards/public';
import { registerInvitationsTranslations } from '@/features/invitations/public';
import { registerCustomFieldsTranslations } from '@/features/custom-fields/public';
import { registerChecklistsTranslations } from '@/features/checklists/public';
import { registerReportsTranslations } from '@/features/reports/public';
import { registerNotificationsTranslations } from '@/features/notifications/public';
import { registerRichTextTranslations } from '@/shared/components/custom/rich-text-editor/locales';
import { registerProjectFilesTranslations } from '@/features/project-files/public';
import { registerLaborPerformanceTranslations } from '@/features/labor-performance/public';
import { registerDashboardTranslations } from '@/features/dashboard/public';
import { registerAnalyticsTranslations } from '@/features/analytics/public';
import { registerWorkSchedulesTranslations } from '@/features/work-schedules/public';
import { registerFormsTranslations } from '@/features/forms/public';
import { registerProjectTemplatesTranslations } from '@/features/project-templates/public';
import { registerNotesTranslations } from '@/features/notes/public';
import { registerPersonalSummaryTranslations } from '@/features/personal-summary/public';
import { registerTimeOffTranslations } from '@/features/time-off/public';
import { registerAiTranslations } from '@/features/ai/public';

export function initializeApp(): void {
  registerCoreTranslations();
  registerAuthTranslations();
  registerLayoutTranslations();
  registerListViewTranslations();
  registerAsyncComboboxTranslations();
  registerOnboardingTranslations();
  registerOrganizationsTranslations();
  registerMembersTranslations();
  registerClientsTranslations();
  registerClientContactsTranslations();
  registerClientMembersTranslations();
  registerCalendarTranslations();
  registerTasksTranslations();
  registerTimeTrackingTranslations();
  registerProjectsTranslations();
  registerOrganizationUnitsTranslations();
  registerPaginationTranslations();
  registerFormFieldTranslations();
  registerColorPickerTranslations();
  registerSettingsTranslations();
  registerTagsTranslations();
  registerBoardsTranslations();
  registerWhiteboardsTranslations();
  registerInvitationsTranslations();
  registerCustomFieldsTranslations();
  registerChecklistsTranslations();
  registerReportsTranslations();
  registerNotificationsTranslations();
  registerRichTextTranslations();
  registerProjectFilesTranslations();
  registerLaborPerformanceTranslations();
  registerDashboardTranslations();
  registerAnalyticsTranslations();
  registerWorkSchedulesTranslations();
  registerFormsTranslations();
  registerProjectTemplatesTranslations();
  registerNotesTranslations();
  registerPersonalSummaryTranslations();
  registerTimeOffTranslations();
  registerAiTranslations();
  setupZodErrorMap();
  configureHttpClient();
}

function configureHttpClient(): void {
  // ==============================
  // 🔐 TOKEN PROVIDER
  // ==============================
  httpClient.setTokenProvider(
    () => {
      const state = useAuthStore.getState();

      if (!state.hasHydrated) return null;
      if (!state.isTokenValid()) return null;

      return state.tokens?.accessToken ?? null;
    },
    () => {
      return useOrganizationStore.getState().activeOrganizationId;
    },
  );

  // ==============================
  // 🔁 REFRESH STRATEGY
  // ==============================
  httpClient.setRefreshHandler(async () => {
    const state = useAuthStore.getState();

    try {
      const tokens = await adapter.refreshSession();
      state.setTokens(tokens);
      return tokens.accessToken;
    } catch (error) {
      state.logout();
      throw error;
    }
  });

  // ==============================
  // 🌍 GLOBAL ERROR HANDLER
  // ==============================
  httpClient.setErrorHandler((error: AxiosError) => {
    if (isCancel(error)) return;
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      return;
    }

    const errorType = classifyHttpError(error);
    const errorCode = extractBackendErrorCode(error);
    const url = error.config?.url ?? '';
    const method = error.config?.method ?? 'GET';

    // TODO: integrate error monitoring service (e.g. Sentry) — send error + context here

    const { titleKey, descriptionKey } = lookupRegistry(url, method, errorType, errorCode);

    sileo.error({
      title: i18n.t(titleKey),
      description: i18n.t(descriptionKey),
    });
  });

  // ==============================
  // ⏳ REQUEST LIFECYCLE (Loader)
  // ==============================
  httpClient.setRequestLifecycleHandlers({
    onRequestStart: () => {
      // useLoaderStore.getState().show();
    },
    onRequestEnd: () => {
      // useLoaderStore.getState().hide();
    },
  });
}
